import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { Box, Container, useTheme } from '@mui/material'
import { Person, Lock, CreditCard } from '@mui/icons-material'

// Import existing sections (we'll reuse them)
import AboutYou from 'components/Profile/Sections/AboutYou'
import SecuritySection from 'components/Profile/Sections/Security'
import AccountStatus from 'components/Profile/Sections/AccountStatus'

// Import Profile components
import {
  ProfileHeader,
  ProfileSection,
  ProfileTabs,
  ProfileSkeleton,
} from 'components/Profile'

/**
 * EmployerProfile - Modern employer profile with enhanced UX
 * Features:
 * - Tabbed navigation
 * - Profile completion card
 * - Reuses existing section components
 * - Responsive design
 */
const EmployerProfile = () => {
  const theme = useTheme()

  // Redux state
  const employerUser = useSelector((state) => state.employerUser)

  // Tab definitions
  const tabs = [
    { label: 'About You', icon: <Person />, id: 'about-you' },
    { label: 'Security', icon: <Lock />, id: 'security' },
    { label: 'Account', icon: <CreditCard />, id: 'account' },
  ]

  // Local state, honoring ?tab= deep links (e.g. returning from the Stripe billing portal)
  const [searchParams] = useSearchParams()
  const requestedTab = searchParams.get('tab')
  const [activeTab, setActiveTab] = useState(
    tabs.some((tab) => tab.id === requestedTab) ? requestedTab : 'about-you',
  )

  if (employerUser.loading) {
    return <ProfileSkeleton />
  }

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        bgcolor: theme.palette.background.default,
      }}
    >
      {/* Profile Header */}
      <ProfileHeader
        profile={{
          firstName: employerUser.firstName,
          lastName: employerUser.lastName,
          city: employerUser.city,
          state: employerUser.state,
          phone: employerUser.phone,
          profileImage: employerUser.profileImage,
          emailVerifiedAt: employerUser.emailVerifiedAt,
          jobTitle: employerUser.jobTitle,
          about: employerUser.about,
        }}
        profileType="employer"
      />

      {/* Tabbed Navigation */}
      <Box sx={{ position: 'sticky', top: 64, zIndex: 10, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <ProfileTabs activeTab={activeTab} onChange={setActiveTab} tabs={tabs} />
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* About You Section */}
        {activeTab === 'about-you' && (
          <ProfileSection
            id="about-you"
            title="About You"
            icon={<Person />}
            completionStatus={
              employerUser.profileImage &&
              employerUser.city &&
              employerUser.state &&
              employerUser.phone &&
              employerUser.jobTitle &&
              employerUser.about
                ? 'complete'
                : 'incomplete'
            }
          >
            <AboutYou employerUser={employerUser} />
          </ProfileSection>
        )}

        {/* Security Section */}
        {activeTab === 'security' && (
          <ProfileSection id="security" title="Security" icon={<Lock />}>
            <SecuritySection employerUser={employerUser} />
          </ProfileSection>
        )}

        {/* Account Section */}
        {activeTab === 'account' && (
          <ProfileSection id="account" title="Account Status" icon={<CreditCard />}>
            <AccountStatus />
          </ProfileSection>
        )}
      </Container>
    </Box>
  )
}

export default EmployerProfile
